import { useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useRequestDomain } from "@stringf/hooks/queries/domains";
import { Button } from "@stringf/ui/button";
import { Input } from "@stringf/ui/input";
import { Select } from "@stringf/ui/select";
import { FieldLabel } from "@/components/ui/eyebrow";
import { getDomainRequestErrorMessage } from "@/lib/errors";

/*
  "Ask us to add your domain": a Domain request, filed from the Pulse section.

  Deliberately small. The three fields are the three the contract requires, and
  the institution name and account type are hints for the Operator, not facts.
  There is no email field: `requesterEmail` is optional, and the requester is not
  notified (stringr CONTEXT.md, Domain request), so collecting an address would
  imply a reply that never comes. The success copy says "we'll review it" and
  nothing about when, and nothing about being told.

  The domain field forgives what a visitor will actually type: a whole email
  ("me@num.edu.mn"), capitals, stray spaces. The server lower-cases and trims
  too, but it does not take an email apart.
*/

export const DOMAIN_REQUEST_ID = "pulse-domain-request";

const LABEL_DOMAIN = "pulse-request-domain";
const LABEL_INSTITUTION = "pulse-request-institution";
const LABEL_TYPE = "pulse-request-type";

const DOMAIN_SHAPE = /^[\p{L}\p{N}-]+(\.[\p{L}\p{N}-]+)+$/u;

/** "  Me@NUM.edu.mn " -> "num.edu.mn". Not validated: see `DOMAIN_SHAPE`. */
export function normalizeDomain(raw: string): string {
  const trimmed = raw.trim().toLowerCase();
  return trimmed.slice(trimmed.lastIndexOf("@") + 1);
}

type Field = "domain" | "institution" | "accountType";
type AccountType = "STUDENT" | "WORKER";

const FIELD_ID: Record<Field, string> = {
  domain: LABEL_DOMAIN,
  institution: LABEL_INSTITUTION,
  accountType: LABEL_TYPE,
};

const FIELD_HEIGHT = "h-10";
const ERROR = "mt-1.5 text-meta text-danger-text";

export function DomainRequestForm() {
  const { t } = useTranslation();
  const request = useRequestDomain();
  const formRef = useRef<HTMLFormElement>(null);

  const [domain, setDomain] = useState("");
  const [institution, setInstitution] = useState("");
  const [accountType, setAccountType] = useState<AccountType | "">("");
  const [invalid, setInvalid] = useState<Field[]>([]);

  if (request.isSuccess) {
    return (
      <p id={DOMAIN_REQUEST_ID} role="status" className="text-body text-text-2">
        {t("pulse.request.success")}
      </p>
    );
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (request.isPending) return;

    const cleanDomain = normalizeDomain(domain);
    const cleanName = institution.trim();
    const problems: Field[] = [];
    if (!DOMAIN_SHAPE.test(cleanDomain)) problems.push("domain");
    if (!cleanName) problems.push("institution");
    if (accountType === "") problems.push("accountType");
    setInvalid(problems);

    if (problems.length > 0) {
      formRef.current?.querySelector<HTMLElement>(`#${FIELD_ID[problems[0]]}`)?.focus();
      return;
    }
    request.mutate({
      domain: cleanDomain,
      institutionName: cleanName,
      accountType: accountType as AccountType,
    });
  };

  const bad = (field: Field) => invalid.includes(field);
  const described = (field: Field, extra?: string) =>
    [bad(field) ? `${FIELD_ID[field]}-error` : undefined, extra].filter(Boolean).join(" ") ||
    undefined;

  return (
    <form
      id={DOMAIN_REQUEST_ID}
      ref={formRef}
      noValidate
      onSubmit={submit}
      className="max-w-sm space-y-4"
    >
      <div>
        <FieldLabel htmlFor={LABEL_DOMAIN}>{t("pulse.request.domain")}</FieldLabel>
        <Input
          id={LABEL_DOMAIN}
          className={`mt-1.5 ${FIELD_HEIGHT}`}
          autoFocus
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          inputMode="url"
          placeholder="university.edu.mn"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          aria-invalid={bad("domain") || undefined}
          aria-describedby={described("domain", `${LABEL_DOMAIN}-hint`)}
        />
        <p id={`${LABEL_DOMAIN}-hint`} className="mt-1.5 text-meta text-text-3">
          {t("pulse.request.domainHint")}
        </p>
        {bad("domain") && (
          <p id={`${LABEL_DOMAIN}-error`} className={ERROR}>
            {t("pulse.request.error.domain")}
          </p>
        )}
      </div>

      <div>
        <FieldLabel htmlFor={LABEL_INSTITUTION}>{t("pulse.request.institution")}</FieldLabel>
        <Input
          id={LABEL_INSTITUTION}
          className={`mt-1.5 ${FIELD_HEIGHT}`}
          autoComplete="organization"
          value={institution}
          onChange={(e) => setInstitution(e.target.value)}
          aria-invalid={bad("institution") || undefined}
          aria-describedby={described("institution")}
        />
        {bad("institution") && (
          <p id={`${LABEL_INSTITUTION}-error`} className={ERROR}>
            {t("pulse.request.error.institution")}
          </p>
        )}
      </div>

      <div>
        <FieldLabel htmlFor={LABEL_TYPE}>{t("pulse.request.accountType")}</FieldLabel>
        <Select
          id={LABEL_TYPE}
          className={`mt-1.5 ${FIELD_HEIGHT}`}
          value={accountType}
          onChange={(e) => setAccountType(e.target.value as AccountType | "")}
          aria-invalid={bad("accountType") || undefined}
          aria-describedby={described("accountType")}
        >
          <option value="">{t("pulse.request.choose")}</option>
          <option value="STUDENT">{t("pulse.request.student")}</option>
          <option value="WORKER">{t("pulse.request.worker")}</option>
        </Select>
        {bad("accountType") && (
          <p id={`${LABEL_TYPE}-error`} className={ERROR}>
            {t("pulse.request.error.accountType")}
          </p>
        )}
      </div>

      {request.isError && (
        <p role="alert" className="text-body text-danger-text">
          {getDomainRequestErrorMessage(request.error, t)}
        </p>
      )}

      <Button type="submit" variant="outline" size="sm" disabled={request.isPending}>
        {request.isPending ? t("pulse.request.submitting") : t("pulse.request.submit")}
      </Button>
    </form>
  );
}
